import { FormattedMessage } from 'react-intl';

import { Button } from '@shieldpay/bumblebee/button';
import { Card } from '@shieldpay/bumblebee/card';
import { Heading } from '@shieldpay/bumblebee/heading';
import { Column, Row } from '@shieldpay/bumblebee/layout';
import { Link } from '@shieldpay/bumblebee/link';
import CardMarkdown from '@shieldpay/bumblebee/markdown/card.md';
import { Text } from '@shieldpay/bumblebee/text';

import { ContentsTable } from '~/components/contents-table/contents-table';
import { Design } from '~/components/design/design';
import { MdxFormatter } from '~/components/mdx-formatter/mdx-formatter';
import { ComponentPreview } from '~/components/preview/component-preview';
import { createMeta } from '~/utilities/create-meta';

export const meta = createMeta({
  id: 'hive.components.card.title',
});

export const CardComponentPage = () => {
  return (
    <>
      <Heading>
        <FormattedMessage id="hive.components.card.title" />
      </Heading>

      <ContentsTable />

      <ComponentPreview alignContent={{ x: 'left' }}>
        <Card spacing="basePos5">
          <Heading level="3" variant="title">
            Primary
          </Heading>
          <Row spacing="basePos5">
            <Text paragraph>This is an example of a primary card</Text>
            <Button variant="outline">OK, hun</Button>
          </Row>
        </Card>
        <Heading level="3" variant="title">
          Light-shadow
        </Heading>
        <Card
          variant="lightShadow"
          spacing="basePos5"
          alignContent={{ x: 'left' }}
        >
          <Column>
            <Text paragraph>This is an example of a</Text>
            <Text paragraph>Light-shadow card</Text>
          </Column>

          <Link href="/">More info</Link>
        </Card>
        <Heading level="3" variant="title">
          Dark-shadow
        </Heading>
        <Card
          variant="darkShadow"
          background="neutral150"
          padding={{ x: 'basePos3', y: 'basePos1' }}
        >
          <Text paragraph>
            This is an example of a Dark-shadow card
            <br />
            with custom padding and background
          </Text>
        </Card>
      </ComponentPreview>
      <Design>
        <Design.Section heading="Usage">
          <Text paragraph>
            The card component is what gets used behind the content of any
            self-contained components. These would be components that have a
            background or frame such as a popover or dialog box.
          </Text>
          <Text paragraph>
            Due to the way that components are built in Figma, we will not be
            adding this to the library. Components that have a card will be
            designed within the frame of the component itself.
          </Text>
        </Design.Section>
        <Design.Section heading="Specs">
          <Text paragraph>
            The card component consists of the blank rectangle with a 4px radius
            on all corners. The colour and styles of the card vary depending on
            the context of its usage and how elevated it is.
          </Text>
          <Text paragraph>
            The contents within the card component should adhere to the spacing
            guidelines in the specs. However these may differ between
            components.
          </Text>
          <Text paragraph>There are three types of card components:</Text>
          <Column>
            <Heading level="5" variant="title">
              Primary
            </Heading>
            <Text paragraph>
              This is the main card behind UI on the ground level.
              <br />
              Example: Balance card
            </Text>
          </Column>
          <Text paragraph>
            Style:
            <br />
            White with a 1px
            <br />
            Primary-lighten-30 stroke. No drop shadow.
          </Text>
          <Column>
            <Heading level="5" variant="title">
              Light-shadow
            </Heading>
            <Text paragraph>
              This is the card used for any components that are elevated on
              mezzanine or higher.
              <br />
              Example: User menu (top bar).
            </Text>
          </Column>
          <Text paragraph>
            Style:
            <br />
            White with a 1px Primary-lighten-30 stroke.
          </Text>
          <Text paragraph>
            Drop shadow:
            <br />
            X:0px / Y:2px / Blur: 8px / Spread: 0px
            <br />
            Primary-base at 50% opacity
          </Text>
          <Column>
            <Heading level="5" variant="title">
              Dark-shadow
            </Heading>
            <Text paragraph>
              This is the card used for any components that are elevated on
              mezzanine or higher.
              <br />
              Example: Toast notification
            </Text>
          </Column>
          <Text paragraph>
            Style:
            <br />
            Can be multiple colours but no stroke
          </Text>
          <Text paragraph>
            Drop shadow:
            <br />
            X:0px / Y:2px / Blur: 8px / Spread: 0px
            <br />
            Primary-base at 50% opacity
          </Text>
          <Text paragraph>
            Please refer to <Text variant="bodyMedium150">Specs</Text> for
            examples
          </Text>
        </Design.Section>
        <Design.Section heading="Placement">
          <Text paragraph>
            The card component will have various placements. These will be
            defined in the component documentation.
          </Text>
        </Design.Section>
        <Design.Section heading="Accessibility">
          <Text paragraph>
            Use the appropriate ARIA attributes for screen readers where
            applicable.
          </Text>
        </Design.Section>
      </Design>

      <MdxFormatter md={CardMarkdown} />
    </>
  );
};

export default CardComponentPage;
